import React, { useState, useEffect } from 'react';
import { useCart } from './CartContext';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../contexts/CurrencyContext';

const CartItem = ({ item }) => {
  const { updateQuantity, removeFromCart } = useCart();
  const { t } = useTranslation();

  // Get size display text from variation attributes
  const getSizeDisplay = () => {
    // First check if we have variation with attributes
    if (item.variation && item.variation.attributes && Array.isArray(item.variation.attributes)) {
      const sizeAttr = item.variation.attributes.find(attr =>
        attr && attr.name && attr.name.toLowerCase().includes('size')
      );
      if (sizeAttr && sizeAttr.option) {
        return sizeAttr.option;
      }
    }

    // Check if variation has a name (like "52 ml / 1.69 oz.")
    if (item.variation && item.variation.name && item.variation.name !== item.name) {
      return item.variation.name;
    }

    // Fallback to old selectedSize logic
    if (item.selectedSize) {
      return item.selectedSize === 1 ? '50 ml' : '100 ml';
    }

    return null;
  };

  const handleRemove = () => {
    removeFromCart(item.id, item.selectedSize, item.variation_id);
  };

  const handleUpdateQuantity = (newQuantity) => {
    updateQuantity(item.id, newQuantity, item.selectedSize, item.variation_id);
  };

  return (
    <div className="flex py-2">
      {/* Product Image - Square on the left */}
      <div className="flex-shrink-0 w-[167px] h-[167px] border border-black mr-4">
        {item.image ? (
          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[#BDBDBD]"></div>
        )}
      </div>

      {/* Product Details and Controls */}
      <div className="flex-1 flex flex-col justify-between">
        {/* Product Info */}
        <div>
          <h3 className="text-14 font-normal text-black mb-1">{item.name}</h3>
          {item.formula && (
            <p className="text-14 font-light pt-[4px]">
              ({item.formula})
            </p>
          )}
          {getSizeDisplay() && (
            <p className="text-14 font-light pt-[4px]">
              {getSizeDisplay()}
            </p>
          )}
        </div>

        {/* Remove Link */}
        <div>
          <div className="mb-2">
            <button
              className="text-14 font-light text-black underline"
              onClick={handleRemove}
            >
              {t('cart.remove')}
            </button>
          </div>

          {/* Quantity Controls - Full width box */}
          <div className="flex border border-black">
            <button
              className={`pl-[9px] py-[12px] text-center bg-white `}
              onClick={() => {
                if (item.quantity > 1)
                  handleUpdateQuantity(item.quantity - 1)
              }}
            >
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_2005_349)">
                  <path d="M7.6365 4H0.36377" stroke={`${item.quantity > 1 ? '#000000' : '#BDBDBD'}`} strokeMiterlimit="10" />
                </g>
                <defs>
                  <clipPath id="clip0_2005_349">
                    <rect width="8" height="8" fill="white" />
                  </clipPath>
                </defs>
              </svg>

            </button>
            <div className="flex-1 pt-[9px] pb-[8px] text-center bg-white text-black text-14 font-normal">
              {item.quantity}
            </div>
            <button
              className="pr-[9px] py-[12px] text-center bg-white text-black"
              onClick={() => handleUpdateQuantity(item.quantity + 1)}
            >
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_2005_352)">
                  <path d="M4 0.363632V7.63636" stroke="black" strokeMiterlimit="10" />
                  <path d="M7.6365 4H0.36377" stroke="black" strokeMiterlimit="10" />
                </g>
                <defs>
                  <clipPath id="clip0_2005_352">
                    <rect width="8" height="8" fill="white" />
                  </clipPath>
                </defs>
              </svg>

            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CartSidebar = () => {
  const { cart, closeCart } = useCart();
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const [isVisible, setIsVisible] = useState(false);

  // Animation styles
  const animationStyles = `
    <style>
      .cart-sidebar-container {
        transform: translateX(100%);
        opacity: 0;
        transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
        -webkit-backface-visibility: hidden;
        -webkit-transform-style: preserve-3d;
        -webkit-perspective: 1000;
      }
      .cart-sidebar-container.open {
        transform: translateX(0);
        opacity: 1;
      }
      .cart-overlay {
        opacity: 0;
        transition: opacity 0.3s ease-in-out;
      }
      .cart-overlay.open {
        opacity: 0.8;
      }
    </style>
  `;

  // Handle cart open/close with animation
  useEffect(() => {
    if (cart.isOpen) {
      // Force reflow to ensure CSS applies
      setTimeout(() => {
        setIsVisible(true);
      }, 10);
    } else {
      setIsVisible(false);
    }
  }, [cart.isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      closeCart();
    }, 300);
  };

  if (!cart.isOpen) return null;

  return (
    <>
      {animationStyles && <div dangerouslySetInnerHTML={{ __html: animationStyles }} />}
      <div className="fixed inset-0 z-50 overflow-hidden">
        {/* Overlay */}
        <div
          className={`absolute inset-0 bg-black cart-overlay ${isVisible ? 'open' : ''}`}
          onClick={handleClose}
        ></div>

        {/* Cart Panel */}
        <div className="absolute inset-y-0 right-0 max-w-full flex">
          <div
            className={`relative w-[358px] cart-sidebar-container ${isVisible ? 'open' : ''}`}
          >
            {/* Cart Content */}
            <div className="h-full flex flex-col bg-white">
              {/* Header */}
              <div className="flex items-center justify-between px-2 pt-[10px] pb-[66px]">
                <h2 className="font-light text-14">
                  {t('cart.title')} ({cart.totalItems})
                </h2>
                <button
                  onClick={handleClose}
                  className="text-black"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_8240_657)">
                      <path d="M14.1748 14.1745L1.82568 1.82544" stroke="black" strokeMiterlimit="10" />
                      <path d="M1.82568 14.1745L14.1748 1.82544" stroke="black" strokeMiterlimit="10" />
                    </g>
                    <defs>
                      <clipPath id="clip0_8240_657">
                        <rect width="16" height="16" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </button>
              </div>

              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto px-2">
                {cart.items.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-black">{t('cart.empty')}</p>
                  </div>
                ) : (
                  <div>
                    {cart.items.map((item, index) => (
                      <CartItem key={`${item.id}-${item.variation_id || item.selectedSize}-${index}`} item={item} />
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              {cart.items.length > 0 && (
                <div className="px-2 pb-[10px]">
                  <hr className='border-t border-black mb-4' />
                  <div className="flex justify-between text-black mb-[4px]">
                    <span className='font-light text-14'>{t('cart.subtotal')}</span>
                    <span className='font-normal text-14'>{formatPrice(cart.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-black mb-4">
                    <span className='font-light text-14'>{t('cart.shipping')}</span>
                    <span className='font-normal text-14'>{t('cart.shippingText')}</span>
                  </div>
                  <Link
                    to="/checkout"
                    onClick={handleClose}
                    className="w-full bg-black text-white pt-[10px] pb-[9px] font-normal text-14 text-center block"
                  >
                    {t('cart.checkout')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CartSidebar;
